import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Form, Button, Icon, Checkbox, Select } from "semantic-ui-react";
import {
  insert_linktree_link,
  upload_linktree_link_file,
} from "../../utils/requestHelper";
import { useDispatch } from "react-redux";
import { fetchLinktree, updateLinktreeLink } from "../../redux/actions";

function removeEmptyStrings(obj) {
  let newObj = {};
  Object.keys(obj).forEach((prop) => {
    if (obj[prop] !== null && obj[prop] !== "") {
      newObj[prop] = obj[prop];
    }
    if (obj[prop] === 0) {
      newObj[prop] = obj[prop];
    }
  });
  return newObj;
}

const NewLinktreeLink = ({ hide, parentId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [data, setData] = useState({ language: null });
  const [uploadFile, setUploadFile] = useState();
  const [errors, setErrors] = useState({});
  const inputFile = useRef(null);

  const languageOptions = [
    { key: "all", text: "Alle Sprachen", value: "" },
    { key: "de", text: "Deutsch", value: "de" },
    { key: "en", text: "Englisch", value: "en" },
  ];

  const handleOnChange = (e) => {
    const { id, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleOnSelectChange = (e, { value, id }) => {
    setData(prevData => ({
      ...prevData,
      [id]: id === "language" && value === "" ? null : value,
    }))
  }

  const handleOnAddFile = () => {
    inputFile.current.click();
  };

  const handleOnFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_FILE_SIZE_MB = 120;
    const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE) {
      alert(`File is too big! Max ${MAX_FILE_SIZE_MB}MB allowed.`);
      return;
    }

    setUploadFile(file);

    setData((prev) => ({
      ...prev,
      url: file.name,
    }));
  };

  const handleOnSubmit = async () => {
    if (data) {
      let obj = removeEmptyStrings(data);
      let newErrors = {};
      let error = false;

      const hasUrl = obj.hasOwnProperty("url") && obj.url.trim() !== "";
      const hasFile = !!uploadFile;

      if (!hasUrl && !hasFile) {
        newErrors.urlError = t("input-error-url");
        error = true;
      }

      if (!obj.hasOwnProperty("name")) {
        newErrors.nameError = t("input-error-name");
        error = true;
      }

      setErrors({
        ...newErrors,
      });
      if (error === false) {
        const result = await insert_linktree_link(parentId, data);
        if (result.success === true) {
          if (uploadFile) {
            const response = await upload_linktree_link_file(
              parentId,
              result.data.id,
              uploadFile,
            );
            if (response.success)
              dispatch(
                updateLinktreeLink(parentId, result.data.id, {
                  url: response.fileUrl,
                }),
              );
          }
          dispatch(fetchLinktree(parentId));
          hide();
        }
      }
    }
  };

  return (
    <>
      <Modal.Header>{t(`headline-new_linktree_link`)}</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <Form>
            <Form.Group widths="equal">
              <Form.Input
                id="name"
                error={
                  errors.hasOwnProperty("nameError") && {
                    content: errors.nameError,
                  }
                }
                onChange={handleOnChange}
                required
                label={t(`input-label-name`)}
                placeholder={t(`input-placeholder-name`)}
              />
              <Form.Input
                id="icon"
                onChange={handleOnChange}
                label={
                  <>
                    <span
                      style={{
                        margin: "0 0 .28571429rem 0",
                        color: "rgba(0, 0, 0, .87)",
                        fontSize: ".92857143em",
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      {t(`input-label-icon`)}
                    </span>
                    <a
                      href="https://semantic-ui.com/elements/icon.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {" "}
                      (Semantic UI Icons)
                    </a>
                  </>
                }
                placeholder={t(`input-placeholder-icon`)}
              />
            </Form.Group>
            <Form.Input
              id="language"
              control={Select}
              label={t(`input-label-language`)}
              onChange={handleOnSelectChange}
              options={languageOptions}
              placeholder={t(`input-placeholder-language`)}
            />
            <Form.Input
              id="url"
              error={
                errors.hasOwnProperty("urlError") && {
                  content: errors.urlError,
                }
              }
              onChange={handleOnChange}
              required
              label={t(`input-label-url`)}
              placeholder={t(`input-placeholder-url`)}
              icon="linkify"
              iconPosition="left"
              actionPosition="right"
              action={
                <>
                  <input
                    type="file"
                    id="file"
                    ref={inputFile}
                    style={{ display: "none" }}
                    onChange={handleOnFileChange}
                    accept=".jpg,.jpeg,.webp,.png,.pdf"
                  />
                  <Button
                    primary
                    animated="vertical"
                    onClick={handleOnAddFile}
                    floated="right"
                  >
                    <Button.Content hidden>{t(`button-upload`)}</Button.Content>
                    <Button.Content visible>
                      <Icon name="upload" />
                    </Button.Content>
                  </Button>
                </>
              }
            />
            <Form.Field>
              <Checkbox
                toggle
                checked={!!data?.isActive}
                label={t("input-label-isActive")}
                onChange={(e, { checked }) =>
                  setData((prev) => ({ ...prev, isActive: checked ? 1 : 0 }))
                }
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                toggle
                checked={!!data?.openInNewTab}
                label={t("input-label-openInNewTab")}
                onChange={(e, { checked }) =>
                  setData((prev) => ({
                    ...prev,
                    openInNewTab: checked ? 1 : 0,
                  }))
                }
              />
            </Form.Field>
            <Form.Group widths="equal">
              <Form.Input
                id="validFrom"
                type="Date"
                onChange={handleOnChange}
                label={t(`input-label-validFrom`)}
                placeholder={t(`input-placeholder-validFrom`)}
              />
              <Form.Input
                id="validUntil"
                type="Date"
                onChange={handleOnChange}
                label={t(`input-label-validUntil`)}
                placeholder={t(`input-placeholder-validUntil`)}
              />
            </Form.Group>
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={hide}>
          <Icon name="times" />
          {t(`button-cancel`)}
        </Button>
        <Button onClick={handleOnSubmit} primary>
          <Icon name="check" />
          {t(`button-create`)}
        </Button>
      </Modal.Actions>
    </>
  );
};

export default NewLinktreeLink;
